import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, BookMarked } from "lucide-react";

export default function SaveTemplateDialog({ open, onOpenChange, projectId, defaultName, onSaved }) {
  const [name, setName] = useState(defaultName || "");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/save-as-template`, { name, description });
      toast.success("Saved as reusable template");
      onOpenChange(false);
      setDescription("");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><BookMarked className="h-5 w-5 text-primary" /> Save as template</DialogTitle>
          <DialogDescription>Capture this project's tasks, phases, milestones and dependencies as a reusable playbook for future projects.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="tpl-name">Template name</Label>
            <Input id="tpl-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Software Launch" className="mt-1.5" data-testid="save-template-name-input" />
          </div>
          <div>
            <Label htmlFor="tpl-desc">Description (optional)</Label>
            <Textarea id="tpl-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When should your team reach for this playbook?" className="mt-1.5" data-testid="save-template-desc-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !name.trim()} className="gap-2" data-testid="save-template-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookMarked className="h-4 w-4" />} Save template</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
